import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import ClaimEditor from "./ClaimEditor";
import { billingError, listVisits } from "./billingApi";
import { Button, Field, Pill, StatusPill, inputCls, money, todayStr } from "./ui";

/*
 * Billing > Claims: visits in a date range (provider / location filters) with the note status, whether
 * Athena billed the visit, and our claim status. Opening a visit shows its claim editor.
 */

const NOTE = { APPROVED: ["Signed", "bg-green-100 text-green-700"], Summarized: ["Unsigned", "bg-amber-50 text-amber-700"] };
const CLAIM_FILTERS = [["all", "All"], ["none", "No claim"], ["draft", "Draft"], ["ready", "Ready"], ["generated", "In a file"],
                       ["sent", "Sent"]];

const ClaimsTab = ({ config, initialDate, initialAppointment, toast }) => {
  const [from, setFrom] = useState(initialDate || todayStr());
  const [to, setTo] = useState(initialDate || todayStr());
  const [providerid, setProviderid] = useState("");
  const [departmentid, setDepartmentid] = useState("");
  const [claimFilter, setClaimFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(initialAppointment || null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listVisits({ from, to, providerid, departmentid });
      setRows(r.visits || []);
    } catch (e) {
      toast(billingError(e, "Could not load visits"), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, providerid, departmentid]);

  useEffect(() => { load(); }, [load]);

  const providers = Object.entries(config?.providers || {});
  const facilities = Object.entries(config?.service_facilities || {});
  const shown = rows.filter((r) => {
    if (claimFilter === "all") return true;
    if (claimFilter === "none") return !r.claim_status;
    if (claimFilter === "sent") return ["submitted_athena", "submitted_clearinghouse"].includes(r.claim_status);
    return r.claim_status === claimFilter;
  });

  if (openId) {
    return (
      <div>
        <button type="button" className="text-[13px] text-[#1D2951] mb-3 hover:underline" onClick={() => setOpenId(null)}>
          ← Back to visits
        </button>
        <ClaimEditor appointmentid={openId} config={config} toast={toast} onClose={() => setOpenId(null)} onChanged={load} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-[#E5E7EB] px-4 py-3">
        <Field label="From"><input type="date" className={inputCls()} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" className={inputCls()} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Field label="Provider">
          <select aria-label="Provider" className={inputCls()} value={providerid} onChange={(e) => setProviderid(e.target.value)}>
            <option value="">All providers</option>
            {providers.map(([id, p]) => <option key={id} value={id}>{p.name || `${p.first} ${p.last}`}</option>)}
          </select>
        </Field>
        <Field label="Location">
          <select aria-label="Location" className={inputCls()} value={departmentid} onChange={(e) => setDepartmentid(e.target.value)}>
            <option value="">All locations</option>
            {facilities.map(([id, f]) => <option key={id} value={id}>{(f.name || `Department ${id}`).replace(/^.* - /, "")}</option>)}
          </select>
        </Field>
        <Field label="Claim">
          <select aria-label="Claim status" className={inputCls()} value={claimFilter} onChange={(e) => setClaimFilter(e.target.value)}>
            {CLAIM_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Button kind="outline" onClick={load} disabled={loading}>Refresh</Button>
        <span className="ml-auto text-[12px] text-[#6B7280]">{shown.length} visit{shown.length === 1 ? "" : "s"}</span>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><CircularProgress size={26} /></div>
        ) : shown.length === 0 ? (
          <div className="text-center text-[13px] text-[#6B7280] py-12">No visits for these filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#6B7280] bg-[#F9FAFB]">
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Visit</th><th className="px-3 py-2">Insurance</th><th className="px-3 py-2">Note</th>
                <th className="px-3 py-2">Athena</th><th className="px-3 py-2">Claim</th><th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const note = NOTE[r.note_status];
                return (
                  <tr key={r.appointmentid} className="border-t border-[#F3F4F6] hover:bg-[#EEF2FB] cursor-pointer"
                    onClick={() => setOpenId(r.appointmentid)}>
                    <td className="px-3 py-2 whitespace-nowrap text-[#374151]">{r.dos} <span className="text-[#9CA3AF]">{r.time}</span></td>
                    <td className="px-3 py-2 text-[#18181B] font-medium">{r.patient_name || `Patient ${r.patientid}`}</td>
                    <td className="px-3 py-2 text-[#374151]">{r.providername}</td>
                    <td className="px-3 py-2 text-[#374151]">{r.tov || r.purpose}{r.lov ? ` · L${r.lov}` : ""}</td>
                    <td className="px-3 py-2 text-[12px] text-[#6B7280] max-w-[200px] truncate" title={r.insurance}>{r.insurance}</td>
                    <td className="px-3 py-2">{note ? <Pill className={note[1]}>{note[0]}</Pill> : <span className="text-[12px] text-[#9CA3AF]">None</span>}</td>
                    <td className="px-3 py-2 text-[12px]">{r.athena_billed ? <Pill className="bg-[#F3F4F6] text-[#374151]">Billed</Pill> : ""}</td>
                    <td className="px-3 py-2"><StatusPill status={r.claim_status} /></td>
                    <td className="px-3 py-2 text-right text-[#374151]">{r.claim_total ? money(r.claim_total) : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ClaimsTab;
